import { AppData, Lightning, Log, Utils } from "@lightningjs/sdk";
import { AppSetup } from "../lib/app-setup";
import { fetchData, postLikeDislikeUrl } from "../lib/models/data-service";
import { Logger } from "../lib/logger";
import Router from "@lightningjs/sdk/src/Router";

export default class VideoFeedbackControls extends Lightning.Component {

    itemId = "";
    selectedIndex = 0;
    likeVal = 0;
    isFocused = false;

    options = [
        { id: 1, label: "Like it!", src: "images/thumb-up.png", focusedSrc: "images/hover-thumb-up.png" },
        { id: 2, label: "Love it!", src: "images/love.png", focusedSrc: "images/hover-love.png" },
        { id: 3, label: "Not For Me!", src: "images/thumb-down.png", focusedSrc: "images/hover-thumb-down.png" },
    ];

    static override _template() {
        return {
            FeedbackControl: {
                w: 130,
                h: 100,
                collision: true,
                flex: { direction: "row", alignItems: "center", justifyContent: "space-between" },
                children: [],
            }
        };
    }

    override _init() {
        const FeedbackControl = this.options.map((opt, index) => ({
            ref: `Thumb${index}`,
            w: 130,
            h: 100,
            collision: true,
            Icon: {
                w: 70,
                h: 70,
                flex: { direction: "column", alignItems: "center" },
                texture: {
                    type: Lightning.textures.ImageTexture,
                    src: Utils.asset(opt.src),
                    resizeMode: { type: "contain", w: 70, h: 70 },
                }
            },
            Label: {
                mountX: 0.5,
                x: 35,
                y: 76,
                alpha: 0,
                text: {
                    text: opt.label,
                    fontSize: 18,
                    textColor: 0xFFFFFFFF,
                    textAlign: "center",
                },
            },
        }));

        this.tag("FeedbackControl").patch({ children: FeedbackControl });
        this.selectedIndex = -1; 
        this._updateFocus();
    }

    setVideoItem(nodeId: any, likeVal: any) {
        this.itemId = nodeId;
        this.likeVal = Number(likeVal);
        this.selectedIndex = 0;
        this._updateFocus();
    }

    override _focus() {
         this.isFocused = true;
        this.selectedIndex = 0;
        this._updateFocus();
        this._announceSelected();
    }

    override _unfocus() {
         this.isFocused = false;
        this.selectedIndex = -1;
        this._updateFocus();
    }

    _announceSelected() {
        const opt = this.options[this.selectedIndex];
        if (!opt) return;
        const { platform } = AppData as AppSetup;
        platform?.textToSpeach(opt.id === this.likeVal ? `${opt.label}, Selected` : opt.label);
    }

    _updateFocus() {
        const feedbackControl = this.tag("FeedbackControl");
        const likedIndex = this.likeVal === 0
            ? -1
            : this.options.findIndex(opt => opt.id === this.likeVal);

        this.options.forEach((opt, index) => {
            const container = feedbackControl.children[index];
            if (!container) return;

            const isLiked = index === likedIndex;
            const isFocused =  this.isFocused && index === this.selectedIndex;
            const useFocused = isFocused || isLiked;

            container.tag("Icon").patch({
                texture: {
                    type: Lightning.textures.ImageTexture,
                    src: Utils.asset(useFocused ? opt.focusedSrc : opt.src),
                    resizeMode: { type: "contain", w: 70, h: 70 },
                },
            });

            container.tag("Label").patch({
                alpha: isFocused ? 1 : 0,
            });
        });
    }


    _getThumbIndex(target: any): number {
        if (!target || typeof target.__ref !== "string") return -1;
        const match = target.__ref.match(/^Thumb(\d)$/);
        return match ? parseInt(match[1], 10) : -1;
    }

    _handleHover(target: any) {
        const index = this._getThumbIndex(target);
        if (index !== -1) {
            this.selectedIndex = index;
            this._updateFocus();
        }
    }

    _handleUnhover() {
        this.selectedIndex = -1;
        this._updateFocus();
    }

    _handleClick(target: any) {
        const index = this._getThumbIndex(target);
        if (index !== -1) {
            this.selectedIndex = index;
            this.isFocused = true;
            this._updateFocus();
            this._announceSelected();
            this._handleEnter();
        }
    }

    override _handleLeft() {
        if (this.selectedIndex > 0) {
            this.selectedIndex--;
            this._updateFocus();
            this._announceSelected();
        }
        return true;
    }

    override _handleRight() {
        if (this.selectedIndex < this.options.length - 1) {
            this.selectedIndex++;
            this._updateFocus();
            this._announceSelected();
            return true;
        }
        return false;
    }

    override _handleEnter() {
         const { appState, platform } = AppData as AppSetup
        const app = appState?.getApp()
        const grid = app?.navigation.getCurrentGrid()
        const selectValue = this.options[this.selectedIndex].id;
        const newItemVal = (this.likeVal === selectValue) ? 0 : selectValue;
        const itemType = "node";
        const url = postLikeDislikeUrl(this.itemId, itemType, newItemVal);

        let notificationMessage = "Rating removed!";
        if (newItemVal === 1) {
            notificationMessage = "Liked!";
            Logger.processUserAction('VideoLiked');
        } else if (newItemVal === 2) {
            notificationMessage = "Loved it!";
            Logger.processUserAction('VideoLoved');
        } else if (newItemVal === 3) {
            notificationMessage = "Not for me!";
            Logger.processUserAction('VideoDisliked');
        }

        platform?.textToSpeach(notificationMessage);

        this.likeVal = newItemVal;
        this._updateFocus();

        if (this.likeVal !== undefined && this.likeVal !== null) {
            grid?.updateItemLikeStatus(this.likeVal, false)
        }

        fetchData(url)
            .then((response) => {
                if (response.data.status === 1) {
                    Log.info("RateContentWidget:", this.itemId, itemType, newItemVal);
                    Router.getActivePage()?.widgets.notification.showNotification(notificationMessage);
                } else {
                    Log.error("RateContentWidget: API error:", response.data.message);
                }
            })
            .catch((err) => {
                Log.error("RateContentWidget: API call failed:", err.message);
            });

        this.signal('close', this.likeVal);
        this.fireAncestors('$videoFeedbackSelected');
        return true;
    }
}